import React, { useState, useEffect } from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Book } from 'lucide-react';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { useIsMobile } from '@/hooks/use-mobile';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { KnowledgeBase as AppKnowledgeBase } from '@/types';
import { KnowledgeBase as SupabaseKnowledgeBase } from '@/types/supabase';

interface ChatInputFieldProps {
  message: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  hasAttachments: boolean;
  isLoading: boolean;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSelectKnowledgeBase?: (kbId: string, kbName: string) => void;
}

const ChatInputField: React.FC<ChatInputFieldProps> = ({
  message,
  textareaRef,
  hasAttachments,
  isLoading,
  onTextChange,
  onKeyDown,
  onSubmit,
  onSelectKnowledgeBase
}) => {
  const { knowledgeBases, loading: kbLoading, fetchKnowledgeBases } = useKnowledgeBases();
  const [showKbSuggestions, setShowKbSuggestions] = useState(false);
  const [filteredKnowledgeBases, setFilteredKnowledgeBases] = useState<SupabaseKnowledgeBase[]>([]);
  const [selectedKbIndex, setSelectedKbIndex] = useState(0);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);
  
  useEffect(() => {
    const kbCommandRegex = /^\/kb(?:\s+)?/i;
    if (kbCommandRegex.test(message)) {
      console.log('KB command detected:', message);
      const searchTerm = message.replace(/^\/kb\s*/i, '').toLowerCase();
      
      if (knowledgeBases && Array.isArray(knowledgeBases)) {
        const filtered = searchTerm 
          ? knowledgeBases.filter(kb => kb.name.toLowerCase().includes(searchTerm))
          : knowledgeBases;
          
        console.log('Filtered knowledge bases:', filtered);
        setFilteredKnowledgeBases(filtered);
        setShowKbSuggestions(true);
        setSelectedKbIndex(0);
      }
    } else {
      setShowKbSuggestions(false);
    }
  }, [message, knowledgeBases]);

  const handleKeyboardNavigation = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showKbSuggestions && filteredKnowledgeBases.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedKbIndex(prev => (prev + 1) % filteredKnowledgeBases.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedKbIndex(prev => 
          prev > 0 ? prev - 1 : filteredKnowledgeBases.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectKnowledgeBase(filteredKnowledgeBases[selectedKbIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowKbSuggestions(false);
      }
    }
  };

  const selectKnowledgeBase = (kb: SupabaseKnowledgeBase) => {
    if (!onSelectKnowledgeBase) return;
    
    console.log(`Selected knowledge base: ${kb.name} (${kb.id})`);
    setShowKbSuggestions(false);
    
    if (textareaRef.current) {
      textareaRef.current.value = '';
      textareaRef.current.focus();
    }
    
    onSelectKnowledgeBase(kb.id, kb.name);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showKbSuggestions) {
      handleKeyboardNavigation(e);
    } else {
      onKeyDown(e);
    }
  };

  return (
    <div className="relative w-full">
      <Textarea
        ref={textareaRef}
        placeholder={isMobile ? "Type a message..." : "Type a message or /kb to use a knowledge base..."}
        value={message}
        onChange={onTextChange}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        className="min-h-[50px] resize-none pr-16 py-3"
        rows={1}
      />
      
      <div className="absolute right-2 bottom-1">
        <Button 
          size="sm" 
          className="h-8 w-8 p-0 rounded-full" 
          disabled={isLoading || (!message.trim() && !hasAttachments)}
          onClick={onSubmit}
        >
          <Send size={16} className="text-white" />
        </Button>
      </div>
      
      {showKbSuggestions && (
        <div className="absolute z-10 w-full max-w-md bg-card border border-border rounded-md shadow-md bottom-full mb-2">
          <div className="text-sm font-medium p-2 border-b">
            Knowledge Bases
          </div>
          
          {filteredKnowledgeBases.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No knowledge bases found
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto">
              {filteredKnowledgeBases.map((kb, index) => (
                <div
                  key={kb.id}
                  className={`p-2 text-sm cursor-pointer flex items-center gap-2 ${
                    index === selectedKbIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                  onClick={() => selectKnowledgeBase(kb)}
                >
                  <Book size={16} />
                  <div>
                    <div className="font-medium">{kb.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {kb.documents_count} document{kb.documents_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInputField;